import { TextField, Tooltip } from "@material-ui/core";
import React, { useState, useRef, useMemo } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { get } from "lodash";
import CommonModal from "../common/Modal";
import { If, Then } from "react-if";
import { INSPECT, STATUS, SUBMIT } from "../../constants";
import InfoIcon from "@mui/icons-material/Info";
import { MenuItem, InputAdornment, Paper, IconButton, Popper, ClickAwayListener, CircularProgress } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClearIcon from "@mui/icons-material/Clear";
import { FixedSizeList as List } from "react-window";

const ITEM_HEIGHT = 36; // Height of each menu item in pixels
const MAX_VISIBLE_ITEMS = 7; // Maximum items visible without scrolling

// Virtualized row component for react-window
const VirtualizedRow = React.memo(({ index, style, data }) => {
  const { options, selectedValue, onItemClick } = data;
  const option = options[index];
  
  return (
    <MenuItem
      style={{
        ...style,
        padding: "8px 16px",
        fontSize: "14px",
        boxSizing: "border-box",
        backgroundColor: option === selectedValue ? "rgba(25, 118, 210, 0.08)" : "transparent",
      }}
      onClick={() => onItemClick(option)}
      sx={{
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      {option}
    </MenuItem>
  );
});


export default function StudyNumberComponent({ extra, actions, status, shippingActionType, studyNumberList }) {
  const { t } = useTranslation();
  const [studyDetailsLoading, setStudyDetailsLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isListLoading, setIsListLoading] = useState(true); // Track if dropdown list is loading
  const anchorRef = useRef(null);
  const lastFetchedValueRef = useRef(null);
  const userSelectedFromDropdownRef = useRef(false); // Track if user explicitly selected

  const studyDetailsEditEnabled = ["", STATUS.INPUT, STATUS.REGULATORY_REVIEWER_1, STATUS.REGULATORY_REVIEWER_2].includes(status);

  // Track when studyNumberList is loaded
  React.useEffect(() => {
    if (studyNumberList !== undefined && studyNumberList !== null) {
      // Add a small delay to ensure smooth UI transition
      const timer = setTimeout(() => {
        setIsListLoading(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [studyNumberList]);

  // Don't use memo to ensure fresh list on every render, especially on navigation
  const studyNumberOptions = useMemo(() => {
    return studyNumberList && studyNumberList?.length > 0 
      ? [...new Set(studyNumberList)] 
      : [];
  }, [studyNumberList]);

  // Filter options based on search text - searches ALL records
  const filteredOptions = useMemo(() => {
    if (!searchText) return studyNumberOptions;
    const searchLower = searchText?.toLowerCase();
    return studyNumberOptions?.filter(opt => 
      (opt || "")?.toLowerCase()?.includes(searchLower)
    );
  }, [studyNumberOptions, searchText]);

  // Calculate list height based on number of items
  const listHeight = useMemo(() => {
    const itemCount = filteredOptions?.length;
    return Math?.min(itemCount, MAX_VISIBLE_ITEMS) * ITEM_HEIGHT;
  }, [filteredOptions?.length]);

  const handleStudyNumberSearch = async () => {
    if (!extra?.study_number || extra?.study_number === "Not Applicable") return;
    setStudyDetailsLoading(true);
    try {
      const returndata = await actions.searchByStudyNumber(extra?.study_number);
      const fetchedStudyNumber =
        returndata?.data?.study_number ||
        returndata?.study_number ||
        returndata?.data?.Study_number ||
        returndata?.Study_number;
      if (fetchedStudyNumber) {
        setShowPopup(true);
      } else {
        toast.error(t("commonlabels.emptyDataSourceMessage"));
      }
    } catch (e) {
      toast.error(t("shippingrequest.study_metadata_fetch_error"));
    } finally {
      setStudyDetailsLoading(false);
    }
  };

  // Auto-fetch metadata when user selects an existing option from dropdown
  const handleStudyNumberSelect = async (selected) => {
    // Only fetch if the selected value is not empty
    if (!selected || selected === "Not Applicable") return;

    setStudyDetailsLoading(true);
    try {
      const returndata = await actions.searchByStudyNumber(selected);
      const studyData = get(returndata, "data", returndata);
      const fetchedNumber = get(studyData, "study_number") || get(studyData, "Study_number");
      if (fetchedNumber) {
        actions.setExtraData({
          ...extra,
          study_number: selected,
          studyNumberDetails: studyData,
        });
        lastFetchedValueRef.current = selected;
      } else {
        toast.error(t("shippingrequest.study_metadata_fetch_error"));
      }
    } catch (e) {
      toast.error(t("shippingrequest.study_metadata_fetch_error"));
    } finally {
      setStudyDetailsLoading(false);
    }
  };

  const showStudyNumberDetails = (e) => {
    e.preventDefault();
    if (extra?.studyNumberDetails?.study_number) setShowPopup(true);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    userSelectedFromDropdownRef.current = false; // Reset selection flag when typing
    
    // Clear study details when user types
    actions.setExtraData({
      ...extra,
      study_number: value,
      studyNumberDetails: null,
    });
    lastFetchedValueRef.current = null;
    
    // Open menu when typing
    if (!menuOpen) {
      setMenuOpen(true);
    }
  };

  const handleInputFocus = () => {
    setMenuOpen(true);
  };

  const handleClickAway = () => {
    // Only clear if menu is open and user didn't explicitly select from dropdown
    if (menuOpen && !userSelectedFromDropdownRef.current) {
      setSearchText("");
      actions.setExtraData({
        ...extra,
        study_number: "",
        studyNumberDetails: null,
      });
    }
    setMenuOpen(false);
  };

  const handleMenuItemClick = async (selectedValue) => {
    userSelectedFromDropdownRef.current = true; // Mark as explicitly selected
    setMenuOpen(false);
    setSearchText(selectedValue);
    
    // Update state with the selected value
    actions.setExtraData({
      ...extra,
      study_number: selectedValue,
      studyNumberDetails: selectedValue === "Not Applicable" ? null : extra?.studyNumberDetails,
    });

    // Auto-fetch metadata for selected value
    if (selectedValue && selectedValue !== "Not Applicable" && lastFetchedValueRef.current !== selectedValue) {
      setStudyDetailsLoading(true);
      try {
        const returndata = await actions.searchByStudyNumber(selectedValue);
        const studyData = get(returndata, "data", returndata);
        const fetchedNumber = get(studyData, "study_number") || get(studyData, "Study_number");
        if (fetchedNumber) {
          actions.setExtraData({
            ...extra,
            study_number: selectedValue,
            studyNumberDetails: studyData,
          });
          lastFetchedValueRef.current = selectedValue;
        } else {
          toast.error(t("shippingrequest.study_metadata_fetch_error"));
        }
      } catch (e) {
        toast.error(t("shippingrequest.study_metadata_fetch_error"));
      } finally {
        setStudyDetailsLoading(false);
      }
    }
  };

  // Sync searchText with extra.study_number when component receives new props
  React.useEffect(() => {
    if (extra?.study_number !== searchText) {
      setSearchText(extra?.study_number || "");
      // If there's already a value with studyNumberDetails, mark as selected
      if (extra?.study_number && extra?.studyNumberDetails) {
        userSelectedFromDropdownRef.current = true;
      }
    }
  }, [extra?.study_number]);

  // Handle clear button click
  const handleClear = (e) => {
    e.stopPropagation();
    setSearchText("");
    setMenuOpen(false);
    lastFetchedValueRef.current = null;
    userSelectedFromDropdownRef.current = false;
    actions.setExtraData({
      ...extra,
      study_number: "",
      studyNumberDetails: null,
    });
  };

  const EditModeFooter = () => (
    <div>
      <button
        className="btn btn-primary"
        onClick={() => {
          actions.setExtraData({
            ...extra,
            studyNumberDetails: {
              ...extra?.studyNumberDetails,
              id: "temp_id",
            },
          });
          setShowPopup(false);
        }}
      >
        {t("commonlabels.saveButton")}
      </button>{" "}
      <button className="btn btn-secondary" onClick={() => setShowPopup(false)}>
        {t("commonlabels.cancelbutton")}
      </button>
    </div>
  );

  return (
    <div data-name="study_number_component">
      {(shippingActionType === INSPECT || shippingActionType === SUBMIT) ? (
        extra?.study_number === "null" ? "" : (
          <div>
            {extra?.study_number}
            <a href="" onClick={showStudyNumberDetails}></a>
          </div>
        )
      ) : (
        <div className="d-flex">
          <ClickAwayListener onClickAway={handleClickAway}>
            <div style={{ position: "relative", width: "100%" }} ref={anchorRef}>
              <TextField
                id="study_number"
                name="study_number"
                variant="outlined"
                size="small"
                margin="normal"
                fullWidth
                placeholder= {t("shipping_request.study_number_placeholder")}
                value={searchText}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                disabled={status === STATUS.IN_DELIVERY}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" style={{ marginRight: "-8px" }}>
                      {searchText && (
                        <IconButton
                          size="small"
                          onClick={handleClear}
                          style={{ padding: "2px" }}
                          disabled={status === STATUS.IN_DELIVERY}
                        >
                          <ClearIcon style={{marginRight: '3px'}}/>
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (status !== STATUS.IN_DELIVERY) {
                            setMenuOpen(!menuOpen);
                          }
                        }}
                        style={{ padding: "2px" }}
                        disabled={status === STATUS.IN_DELIVERY}
                      >
                        {/* <ArrowDropDownIcon 
                          style={{ 
                            color: "rgba(0, 0, 0, 0.54)",
                            transform: menuOpen ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.2s"
                          }} 
                        /> */}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Popper
                open={menuOpen}
                anchorEl={anchorRef.current}
                placement="bottom-start"
                style={{ zIndex: 1301, width: anchorRef.current?.offsetWidth || "auto" }}
                modifiers={[
                  {
                    name: "flip",
                    enabled: true,
                    options: {
                      fallbackPlacements: ["top-start", "bottom-start"],
                    },
                  },
                  {
                    name: "preventOverflow",
                    enabled: true,
                    options: {
                      boundary: "viewport",
                      padding: 8,
                    },
                  },
                ]}
              >
                <Paper
                  style={{
                    marginTop: "4px",
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px",
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  {isListLoading ? (
                    <div
                      style={{
                        padding: "20px 16px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      <CircularProgress size={24} />
                      <span style={{ color: "rgba(0, 0, 0, 0.6)", fontSize: "13px" }}>
                        {t("commonlabels.loading") || "Loading..."}
                      </span>
                    </div>
                  ) : filteredOptions.length > 0 ? (
                    <List
                      height={listHeight}
                      itemCount={filteredOptions.length}
                      itemSize={ITEM_HEIGHT}
                      width="100%"
                      itemData={{
                        options: filteredOptions,
                        selectedValue: extra?.study_number,
                        onItemClick: handleMenuItemClick,
                      }}
                    >
                      {VirtualizedRow}
                    </List>
                  ) : (
                    <div
                      style={{
                        padding: "14px 16px",
                        color: "rgba(0, 0, 0, 0.6)",
                        fontSize: "14px",
                      }}
                    >
                      {t("shipping_request.study_number_no_records")}
                    </div>
                  )}
                </Paper>
              </Popper>
            </div>
          </ClickAwayListener>
          <If condition={extra?.study_number !== "Not Applicable"}>
            <Then>
              <button
                className="btn btn-primary"
                style={{
                  top: "-5px",
                  position: "relative",
                  height: "35px",
                }}
                onClick={() => {
                  if (extra?.studyNumberDetails?.study_number) {
                    setShowPopup(true);
                  } else {
                    handleStudyNumberSearch();
                  }
                }}
                disabled={
                  studyDetailsLoading ||
                  !extra?.study_number ||
                  status === STATUS.IN_DELIVERY
                }
                title={extra?.studyNumberDetails?.study_number ? t("shippingrequest.view_study_details") : t("shippingrequest.fetch_study_details")}
              >
                {studyDetailsLoading ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : (
                  <SearchIcon />
                )}
              </button>
            </Then>
          </If>
        </div>
      )}
      {extra?.studyNumberDetails?.study_number &&
        extra?.studyNumberDetails?.study_number !== "null" && (
          <>
            <a href="" onClick={showStudyNumberDetails}>
              {status === STATUS.IN_DELIVERY
                ? ""
                : t("shippingrequest.study_details_label")}
            </a>
            <If condition={studyDetailsEditEnabled}>
              <Then>
                <Tooltip title={t("shippingrequest.study_details_hover_help")}>
                  <InfoIcon />
                </Tooltip>
              </Then>
            </If>
          </>
        )}
      {/* Removed inline auto-fetch error message */}
      <CommonModal
        show={showPopup}
        header={t("shippingrequest.study_details")}
        body={
          <StudyNumberEditComponent
            actions={actions}
            extra={extra}
            studyDetailsEditEnabled={studyDetailsEditEnabled}
          />
        }
        footer={
          !studyDetailsEditEnabled ? (
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowPopup(false);
              }}
            >
              {t("commonlabels.close")}
            </button>
          ) : (
            <EditModeFooter />
          )
        }
        onCancel={() => {
          setShowPopup(false);
        }}
      />
    </div>
  );
}

const StudyNumberEditComponent = ({
  extra,
  actions,
  studyDetailsEditEnabled,
}) => {
  const { t, i18n } = useTranslation();

  const [fieldConfig, setFieldConfig] = useState({
    study_number: {
      disabled: true,
    },
    study_title: {},
    person_responsible: {},
    intended_for_pivotal_evidence: {},
    study_species: {},
    regulatory_standard: {},
    project_name: {},
    activity_type: {},
  });

  return (
    <>
      <div
        className="row"
        style={{
          background: "white",
        }}
      >
        {Object.keys(fieldConfig).map((field) => {
          return (
            <div className="col-lg-3  col-12 form-outline form-margin" key={field}>
              <label className="form-label">
                {t(`shipping_request.${field}`)}
              </label>
              <TextField
                id={field}
                name={field}
                disabled={
                  !!fieldConfig[field].disabled || !studyDetailsEditEnabled
                }
                margin="normal"
                variant="outlined"
                size="small"
                title={extra?.studyNumberDetails?.[field]}
                value={extra?.studyNumberDetails?.[field] || ""}
                onChange={(e) =>
                  actions.setExtraData({
                    ...extra,
                    studyNumberDetails: {
                      ...extra?.studyNumberDetails,
                      [e.target.name]: e.target.value,
                    },
                  })
                }
              />
            </div>
          );
        })}
      </div>
    </>
  );
};